// The lib's own wrapper, not react-i18next directly: this package is typechecked
// inside Viltrapport as well, whose i18next type augmentation declares its own
// namespaces — so a literal 'map' namespace and its keys fail to typecheck there
// even though they are correct for WeHunt. Every other shared component here goes
// through the wrapper for the same reason.
import { useTranslation } from '../i18n'
import { COLORS } from '../lib/theme'
import {
  PRINT_SCALES,
  estimatePrintWait,
  formatGroundCoverageKm,
  formatPrintScale,
  groundExtentMetres,
} from '../lib/printScale'
import type { PaperFormat, PrintOrientation } from '../lib/printPaper'
import { checkMapPrintLimits } from '../lib/mapPrintLimits'
import type { PageMm } from '../lib/printCustomSheet'
import type { MapPrintJobView } from '../lib/mapPrintClient'
import { safePrintUrl } from '../lib/mapPrintClient'
import { Icon } from './Icon'
import type { PrintSkip } from '../lib/printSkips'
import { useRef } from 'react'
import { usePrintShortcut } from './print/usePrintShortcut'

/**
 * The server-rendered PDF half of the print panel.
 *
 * Presentational only: it holds no fetch, no timer and no job state. The host
 * owns starting a print, polling it and opening the result. Every state
 * (submitting, rendering, finished, failed, aged out) can therefore be rendered
 * in a test without a network.
 *
 * Why it exists at all: a PDF from the print server is drawn in a real
 * cartographic projection at a real scale, so a printed sheet can be measured
 * with a ruler — the country's own national grid where the server has premium
 * data, and Web Mercator over the worldwide raster tiles everywhere else. A
 * browser print of what the screen happens to show cannot do either, which is
 * why there is no longer one.
 */
export interface MapPrintSectionProps {
  scale: number
  onScaleChange: (scale: number) => void
  /**
   * The custom sheet's millimetres, when the surface has one.
   *
   * Threaded through every measurement here — the ground coverage, the wait
   * estimate and the limit check — because without it a custom sheet is
   * measured as whatever named format happens to be selected behind it.
   */
  page?: PageMm
  /**
   * The custom scale, when the user has chosen one. `undefined` means a
   * predefined scale is selected, which is also how this control knows whether
   * it is the selected one — the host clears it when a fixed scale is picked.
   */
  customScale?: number
  onCustomScaleChange?: (scale: number) => void
  /**
   * True when this control is the one map zoom drives. Distinct from selected:
   * the page and the scale can both be custom, and exactly one of them follows
   * the camera while the other holds its value.
   */
  customScaleActive?: boolean
  onActivateCustomScale?: () => void
  /**
   * The chosen sheet, so the panel can say what the print will actually cover.
   * A scale on its own does not answer that: "1:20 000" is arithmetic, "5.9 ×
   * 8.4 km" is the hunting ground.
   */
  format: PaperFormat
  orientation: PrintOrientation
  /** The user's recent prints, newest first. */
  jobs: MapPrintJobView[]
  /** True while a print is being submitted. */
  busy?: boolean
  /**
   * True while the sheet's own projector is still loading, so a request built
   * now could not be clipped to the paper.
   *
   * Its own flag rather than folded into `busy`: `busy` also relabels the
   * button "Starting…" and shows the long-running notice, and neither is true
   * of a print that has not been asked for yet. What this withholds is the
   * click — pressing Create before the projector lands posts the WHOLE ground
   * instead of the sheet, which on a large hunting ground silently exceeds the
   * print server's request-body cap and comes back as a failed job.
   */
  sheetPending?: boolean
  sheetError?: 'probe' | 'projector' | 'catalogue' | null
  onRetrySheet?: () => void
  editPending?: boolean
  pointLimitExceeded?: boolean
  skips?: readonly PrintSkip[]
  /** Why the last submission failed, if it did. */
  error?: string | null
  /** Pins left out of the print because no artwork could be drawn for them. */
  omittedPinCount?: number
  /**
   * CLUB-667: how many pin types the view-settings filter is hiding.
   *
   * Stated here as well as on the map chip because this is the flow that
   * generates the sheet: the filter is set on another surface and persists, so
   * without this the user presses Create on a map they have not looked at since
   * filtering it. Distinct from `omittedPinCount`, which reports a failure.
   */
  hiddenPinTypeCount?: number
  /**
   * True when the base map the surface is drawing cannot be printed on the
   * sheet's CRS — today, the aerial flavour on the worldwide raster fallback
   * (see `baseMapBlocksPrint`).
   *
   * Withholds Create rather than letting the job go: the server accepts it and
   * then fails it, so without this the hunter composes a sheet, waits, and
   * gets an error with nothing in it that points at the layer they picked. The
   * fix is theirs to make and it is one click away on the map, which is why
   * the notice names it.
   */
  baseMapUnavailable?: boolean
  /**
   * True when the print server has said nothing prints at the sheet's centre —
   * no region and no worldwide fallback covers it. Create is withheld and the
   * banner says so, in the words that used to be reserved for a surface with no
   * print support at all.
   */
  printUnavailable?: boolean
  onCreate: () => void
  onOpen: (job: MapPrintJobView) => void
  onReprint: (job: MapPrintJobView) => void
}

export function MapPrintSection({
  scale,
  onScaleChange,
  page,
  customScale,
  onCustomScaleChange,
  customScaleActive = false,
  onActivateCustomScale,
  format,
  orientation,
  jobs,
  busy = false,
  sheetPending = false,
  sheetError = null,
  onRetrySheet,
  editPending = false,
  pointLimitExceeded = false,
  skips = [],
  error,
  omittedPinCount = 0,
  hiddenPinTypeCount = 0,
  baseMapUnavailable = false,
  printUnavailable = false,
  onCreate,
  onOpen,
  onReprint,
}: MapPrintSectionProps) {
  const { t } = useTranslation('map')
  const createButton = useRef<HTMLButtonElement>(null)
  usePrintShortcut(() => {
    const button = createButton.current
    if (button && !button.disabled) button.click()
  })

  // `page` wins over `format`/`orientation` wherever it is given, so a custom
  // sheet is measured by its own millimetres rather than by the named format
  // sitting behind it in the panel's state.
  const sheet = { format, orientation, page }
  const ground = groundExtentMetres({ ...sheet, scale })
  const coverage = formatGroundCoverageKm(ground)
  const wait = estimatePrintWait({ ...sheet, scale })

  // Named formats cannot reach this banner — the hosts clamp the scale to one
  // the sheet allows first, so A0 with 1:100 000 selected never arrives here as
  // a breach. A CUSTOM sheet can: a page and a scale chosen independently span
  // whatever ground they span, and a sheet reaching past one projection zone is
  // refused with a reason rather than sent and rejected after the wait.
  const breach = checkMapPrintLimits({ ...sheet, scale })

  // Why Create is withheld, as the key that says so, or null when it is
  // offered. One derivation for the banner and the button so the two cannot
  // disagree — a disabled button with no message reads as a broken page.
  // A limit breach is stated first where both hold: it is about the sheet the
  // user is looking at, while the base map is a setting on another surface.
  const blockedReason: string | null =
    printUnavailable
      ? 'serverPrint.unavailable'
      : breach !== null
        ? `serverPrint.unsupported.${breach}`
        : baseMapUnavailable
          ? 'serverPrint.unsupported.baseMap'
          : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={labelStyle}>{t('serverPrint.title')}</div>

      <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: COLORS.onSurfaceVariant }}>
        {t('serverPrint.explainer')}
      </p>

      <div>
        <div style={labelStyle}>{t('serverPrint.scale')}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {PRINT_SCALES.map((s) => {
            const selected = s === scale && customScale === undefined
            // A coarse scale on a very large sheet spans more ground than one
            // projection zone covers, so the paper would no longer say where the
            // ground is — see MAX_GROUND_DIMENSION_KM. Not a cost limit: the
            // coarse scales are the CHEAP ones to render. Shown but unavailable,
            // so the limit is legible in the picker rather than discovered after
            // pressing print — and so the paper choice visibly drives it.
            // The REASON, not just the yes/no: a sheet past the server's own
            // page limit disables every scale, and that is the page's fault
            // rather than the ground's, so saying "extent" there would send the
            // user to change the wrong control. No control offers such a page
            // today — every writer goes through `clampPage` or the A-series
            // table — so this arm guards a future paper size rather than a
            // reachable state; the ground breach next to it is reachable now.
            const buttonBreach = checkMapPrintLimits({ ...sheet, scale: s })
            const offered = buttonBreach === null
            return (
              <button
                key={s}
                type="button"
                aria-pressed={selected}
                disabled={!offered}
                title={offered ? undefined : t(`serverPrint.unsupported.${buttonBreach}`)}
                onClick={() => onScaleChange(s)}
                style={{
                  flex: '1 1 auto',
                  minWidth: 60,
                  padding: '0 8px',
                  height: 32,
                  borderRadius: 8,
                  cursor: offered ? 'pointer' : 'not-allowed',
                  border: `1.5px solid ${selected ? COLORS.primary : COLORS.outlineVariant}`,
                  background: selected ? COLORS.primaryContainer : 'transparent',
                  color: selected ? COLORS.primary : COLORS.onSurface,
                  fontSize: 13,
                  fontWeight: 600,
                  opacity: offered ? 1 : 0.4,
                }}
              >
                {formatPrintScale(s)}
              </button>
            )
          })}
          {/* Custom joins the fixed scales in their own row for the same reason
              the custom PAGE joins the paper formats: it is the same choice,
              not a mode. It shows the chosen scale once there is one, so the
              row states the answer rather than only the question. */}
          <button
            type="button"
            // See the paper row's Custom button: both say "Custom" in one
            // dialog until one is chosen, so the unchosen one names itself.
            aria-label={customScale === undefined ? t('serverPrint.customScale') : undefined}
            aria-pressed={customScale !== undefined}
            data-active={customScale !== undefined && customScaleActive ? 'true' : undefined}
            onClick={() => {
              // Seeded from the scale already showing, and only when
              // ARRIVING: a second click is the user handing the camera to this
              // control and must leave the scale alone. The branch that used to
              // run here called `onScaleChange`, which is the FIXED scale's
              // setter and clears the custom one, so the gesture meant to
              // activate the control deselected it instead.
              //
              // The seed is what the frame is composed from only until the
              // camera answers: activating a control is the user asking zoom to
              // drive it, so the host re-derives it in the same tick (see
              // `useSheetFromCamera`). What the seed really buys is a sensible
              // starting shape for a surface with no projector — and a value to
              // show while one loads.
              if (customScale === undefined) onCustomScaleChange?.(scale)
              onActivateCustomScale?.()
            }}
            style={{
              flex: '1 1 auto',
              minWidth: 60,
              padding: '0 8px',
              height: 32,
              borderRadius: 8,
              cursor: 'pointer',
              border: `1.5px solid ${customScale !== undefined ? COLORS.primary : COLORS.outlineVariant}`,
              // Active is a ring OUTSIDE the border, because selected already
              // owns the border and both states have to read at once.
              outline:
                customScale !== undefined && customScaleActive
                  ? `2px solid ${COLORS.primary}`
                  : undefined,
              outlineOffset: 1,
              background: customScale !== undefined ? COLORS.primaryContainer : 'transparent',
              color: customScale !== undefined ? COLORS.primary : COLORS.onSurface,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {/* Prefixed for the same reason as the paper row's Custom pill, and
                here the collision is routine rather than possible: the custom
                series contains every fixed scale, so a custom 1:20 000 sits
                beside a fixed 1:20 000 reading identically. */}
            {customScale !== undefined
              ? `${t('serverPrint.autoPrefix')} ${formatPrintScale(customScale)}`
              : t('serverPrint.custom')}
          </button>
        </div>
      </div>

      {/* What this combination means, in the two terms a hunter cares about: how
          much ground lands on the sheet, and how long they will be waiting. Both
          are derived from paper × scale, so they update as either changes. */}
      <p style={noticeStyle}>
        {t('serverPrint.coverage', { width: coverage.width, height: coverage.height })}
        {' · '}
        {t(`serverPrint.wait.${wait}`)}
      </p>

      {blockedReason !== null && (
        <p style={{ ...noticeStyle, color: COLORS.error, background: COLORS.errorContainer }}>
          {t(blockedReason)}
        </p>
      )}

      <button
        ref={createButton}
        data-map-print-create="true"
        type="button"
        className="wh-btn-filled"
        disabled={busy || sheetPending || sheetError !== null || editPending || pointLimitExceeded || blockedReason !== null}
        onClick={onCreate}
        style={{ height: 36 }}
      >
        {busy ? t('serverPrint.creating') : t('serverPrint.create')}
      </button>

      {sheetPending && <p role="status" style={noticeStyle}>{t('serverPrint.preparingSheet')}</p>}
      {editPending && <p role="status" style={noticeStyle}>{t('serverPrint.finishEdit')}</p>}
      {pointLimitExceeded && <p role="alert" style={noticeStyle}>{t('serverPrint.pointLimit')}</p>}
      {skips.length > 0 && (
        <div style={noticeStyle}>
          <strong>{t('serverPrint.notPrinted')}</strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
            {skips.map((skip, i) => <li key={`${skip.kind}-${i}`}>{t(`serverPrint.skips.${skip.kind}`, { count: skip.count, name: skip.name })}</li>)}
          </ul>
        </div>
      )}
      {sheetError && (
        <div role="alert" style={{ ...noticeStyle, color: COLORS.error }}>
          <p style={{ margin: 0 }}>{t(`serverPrint.sheetError.${sheetError}`)}</p>
          {onRetrySheet && <button type="button" onClick={onRetrySheet}>{t('serverPrint.retry')}</button>}
        </div>
      )}

      {/* The reassurance is the feature: a very large sheet can take half an hour
          on the print server, and the job is persisted precisely so the user is
          not required to keep the page open while it renders. */}
      {busy && (
        <p style={noticeStyle}>{t('serverPrint.longRunning')}</p>
      )}

      {error && (
        <p style={{ ...noticeStyle, color: COLORS.error, background: COLORS.errorContainer }}>
          {error}
        </p>
      )}

      {omittedPinCount > 0 && (
        <p style={noticeStyle}>
          {t('serverPrint.omittedPins', { count: omittedPinCount })}
        </p>
      )}

      {hiddenPinTypeCount > 0 && (
        <p style={noticeStyle}>
          {t('serverPrint.hiddenPinTypes', { count: hiddenPinTypeCount })}
        </p>
      )}

      {jobs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={labelStyle}>{t('serverPrint.recent')}</div>
          {jobs.map((job) => (
            <JobRow
              key={job.id}
              job={job}
              onOpen={() => onOpen(job)}
              onReprint={() => onReprint(job)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function JobRow({
  job,
  onOpen,
  onReprint,
}: {
  job: MapPrintJobView
  onOpen: () => void
  onReprint: () => void
}) {
  const { t } = useTranslation('map')

  const statusKey = {
    Pending: 'serverPrint.status.pending',
    Success: 'serverPrint.status.success',
    Error: 'serverPrint.status.error',
    Expired: 'serverPrint.status.expired',
  }[job.status]

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 10px',
        borderRadius: 8,
        background: COLORS.surfaceContainerLow,
      }}
    >
      <Icon name={job.status === "Success" ? "download" : "clock"} size={16} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.onSurface }}>
          {job.pageLabel} · {formatPrintScale(job.scale)}
        </div>
        <div style={{ fontSize: 11, color: COLORS.onSurfaceVariant }}>{t(statusKey)}</div>
        {/* The server's own reason, verbatim: "page too large" is actionable in a
            way any paraphrase of it would not be. */}
        {job.status === 'Error' && job.message && (
          <div style={{ fontSize: 11, color: COLORS.error }}>{job.message}</div>
        )}
      </div>
      {/* Judged by the same rule the click handler uses, so the button cannot be
          offered for a URL that will then be refused. */}
      {job.status === 'Success' && safePrintUrl(job.url) !== null && (
        <button type="button" className="wh-btn-text" onClick={onOpen}>
          {t('serverPrint.open')}
        </button>
      )}
      {/* An aged-out link is re-printable rather than lost: the print server
          deduplicates identical job descriptions, so a repeat usually returns
          immediately instead of re-rendering. */}
      {(job.status === 'Expired' || job.status === 'Error') && (
        <button type="button" className="wh-btn-text" onClick={onReprint}>
          {t('serverPrint.reprint')}
        </button>
      )}
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 1,
  textTransform: 'uppercase',
  color: COLORS.outline,
  marginBottom: 8,
}

const noticeStyle: React.CSSProperties = {
  margin: 0,
  padding: '8px 10px',
  borderRadius: 8,
  background: COLORS.surfaceContainerLow,
  fontSize: 11,
  lineHeight: 1.5,
  color: COLORS.onSurfaceVariant,
}
